import { useEffect, useRef } from "react";

import {
  flattenConversationPages,
  useChatConversations,
  useSessionUser,
} from "@web-app/api";
import useDispatchStore from "@web-app/store/dispatchStore";

import ChatContainer from "../chatPage/ChatContainer";
import {
  getConversationDisplayImage,
  getConversationDisplayName,
  getOtherParticipants,
} from "../chatPage/chatUtils";

export default function ChatPanel() {
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    isChatPanelOpen,
    activeChatConversationId,
    pendingChatUserId,
    pendingChatOrderId,
    pendingChatOrderDisplayId,
    pendingChatDriverName,
    pendingChatDriverImage,
  } = useDispatchStore();
  const { data: conversationsData } = useChatConversations();
  const conversations = flattenConversationPages(conversationsData);
  const { data: currentUser } = useSessionUser();

  const conversation = conversations.find(
    (conv) => conv.conversation.id === activeChatConversationId,
  );

  const isPendingMode = !activeChatConversationId && !!pendingChatUserId;

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (isChatPanelOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isChatPanelOpen]);

  const handleClose = () => {
    useDispatchStore.setState({
      activeChatConversationId: null,
      activeChatDriverId: null,
      isChatPanelOpen: false,
      isChatWingmanConversation: false,
      pendingChatDriverImage: null,
      pendingChatDriverName: null,
      pendingChatOrderDisplayId: null,
      pendingChatOrderId: null,
      pendingChatUserId: null,
    });
  };

  const handleMinimize = () => {
    if (!currentUser) return;

    const driverParticipant = conversation
      ? getOtherParticipants(conversation, currentUser.id).find(
          (p) => p.role === "DRIVER",
        )
      : undefined;

    const conversationData = {
      conversationId: activeChatConversationId || "",
      driverImage: isPendingMode
        ? pendingChatDriverImage || ""
        : conversation
          ? getConversationDisplayImage(conversation, currentUser.id)
          : "",
      driverName: isPendingMode
        ? pendingChatDriverName || "Driver"
        : conversation
          ? getConversationDisplayName(conversation, currentUser.id)
          : "Driver",
      driverUserId: isPendingMode
        ? pendingChatUserId || undefined
        : driverParticipant?.userId,
      orderAppAvatarUrl: isPendingMode
        ? undefined
        : conversation?.orderAppAvatarUrl,
      orderDisplayId: isPendingMode
        ? pendingChatOrderDisplayId || undefined
        : conversation?.orderDisplayId,
      orderExternalId: isPendingMode
        ? undefined
        : conversation?.orderExternalId,
    };

    if (!conversationData.conversationId) return;

    useDispatchStore.setState((state) => {
      const exists = state.minimizedConversations.some(
        (c) => c.conversationId === conversationData.conversationId,
      );
      return {
        activeChatConversationId: null,
        isChatPanelOpen: false,
        minimizedConversations: exists
          ? state.minimizedConversations
          : [...state.minimizedConversations, conversationData],
      };
    });
  };

  if (!isChatPanelOpen) {
    return null;
  }

  if (!currentUser) {
    return null;
  }

  if (!isPendingMode && (!activeChatConversationId || !conversation)) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className="fixed right-[560px] bottom-4 w-[420px] h-[546px] bg-white shadow-2xl z-40 rounded-xl overflow-hidden transform transition-transform duration-300 ease-in-out"
    >
      <ChatContainer
        conversationId={
          isPendingMode ? undefined : activeChatConversationId || undefined
        }
        driverName={
          isPendingMode
            ? pendingChatDriverName || "Driver"
            : conversation
              ? getConversationDisplayName(conversation, currentUser.id)
              : ""
        }
        driverImage={
          isPendingMode
            ? pendingChatDriverImage || undefined
            : conversation
              ? getConversationDisplayImage(conversation, currentUser.id)
              : undefined
        }
        orderDisplayId={
          isPendingMode
            ? pendingChatOrderDisplayId || undefined
            : conversation?.orderDisplayId || undefined
        }
        orderExternalId={
          isPendingMode ? undefined : conversation?.orderExternalId || undefined
        }
        orderAppAvatarUrl={
          isPendingMode
            ? undefined
            : conversation?.orderAppAvatarUrl || undefined
        }
        participantsWithDetails={
          isPendingMode ? undefined : conversation?.participantsWithDetails
        }
        pendingUserId={
          isPendingMode ? pendingChatUserId || undefined : undefined
        }
        pendingOrderId={
          isPendingMode ? pendingChatOrderId || undefined : undefined
        }
        conversationType={conversation?.conversation.type}
        providerState={conversation?.conversation.providerState}
        onClose={handleClose}
        onMinimize={handleMinimize}
      />
    </div>
  );
}
